<template>
  <div class="wrapper">
    <CCard>
      <CCardHeader>
        <strong>iCare People Detail</strong>
      </CCardHeader>
      <CCardBody>
        <div class="total-card mb-3">
          <div class="total-card-label">Total iCare</div>
          <div class="total-card-value">{{ meta.total || 0 }}</div>
        </div>
        <div class="filter-panel mb-3">
          <CRow class="align-items-end">
            <CCol md="3" class="mb-3">
              <label class="filter-label">Search iCare Name</label>
              <CInput v-model.trim="search" placeholder="Type name and press Enter" @keyup.enter="applyFilters" />
            </CCol>
            <CCol md="3" class="mb-3">
              <label class="filter-label">Zone</label>
              <CSelect :value.sync="zone_id" :options="zoneOptions" />
            </CCol>
            <CCol md="3" class="mb-3">
              <label class="filter-label">Summary Filter</label>
              <CSelect :value.sync="summary_filter" :options="summaryFilterOptions" />
            </CCol>
            <CCol md="3" class="mb-3 text-md-right">
              <CButton color="info" class="mr-2" @click="applyFilters">Apply</CButton>
              <CButton color="secondary" variant="outline" @click="resetFilters">Reset</CButton>
            </CCol>
          </CRow>
          <CRow class="align-items-center">
            <CCol md="12" class="mb-2 mb-md-0">
              <div class="legend-title">LEGEND</div>
              <div class="legend-items">
                <span class="legend-item"><span class="dot dot-green"></span> Pending planted</span>
                <span class="legend-item"><span class="dot dot-yellow"></span> Pending remove planted</span>
                <span class="legend-item"><span class="dot dot-red"></span> Remove visitor / cleanup</span>
              </div>
            </CCol>
          </CRow>
        </div>

        <div class="table-responsive">
          <div class="d-flex align-items-center mb-2">
            <label class="mr-2 mb-0">Rows</label>
            <CSelect
              class="per-page-select"
              :value.sync="per_page_option"
              :options="perPageOptions"
              @change="handlePerPageChange"
            />
          </div>
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Foto iCare</th>
                <th>iCare Name</th>
                <th>Zone / ICC</th>
                <th>ICL Name</th>
                <th>Summary</th>
                <th style="width: 140px">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="6">Loading...</td>
              </tr>
              <tr v-for="item in filteredItems" :key="item.icare_id">
                <td>
                  <img v-if="item.icare_image" class="icare-photo" :src="item.icare_image" alt="icare image" />
                  <div v-else class="icare-photo-placeholder">{{ initial(item.icare_name) }}</div>
                </td>
                <td>
                  <a href="#" @click.prevent="openDetail(item.icare_id)">{{ item.icare_name }}</a>
                </td>
                <td>{{ item.zone_name || '-' }}</td>
                <td>{{ item.icl_name }}</td>
                <td>
                  <div class="summary-wrap">
                    <span
                      class="summary-dot dot-green"
                      :title="summaryTooltip('Pending planted', item.summary && item.summary.pending_planted_count, 'Needs planted follow-up', item.summary && item.summary.last_recalculated_at)"
                    >{{ safeCount(item.summary && item.summary.pending_planted_count) }}</span>
                    <span
                      class="summary-dot dot-yellow"
                      :title="summaryTooltip('Pending remove planted', item.summary && item.summary.pending_remove_planted_count, 'Planted but inactive', item.summary && item.summary.last_recalculated_at)"
                    >{{ safeCount(item.summary && item.summary.pending_remove_planted_count) }}</span>
                    <span
                      class="summary-dot dot-red"
                      :title="summaryTooltip('Remove visitor / cleanup', item.summary && item.summary.pending_remove_visitor_count, 'Visitor with cleanup/removal signal', item.summary && item.summary.last_recalculated_at)"
                    >{{ safeCount(item.summary && item.summary.pending_remove_visitor_count) }}</span>
                  </div>
                </td>
                <td>
                  <CButton size="sm" color="info" @click="openDetail(item.icare_id)">Detail</CButton>
                </td>
              </tr>
              <tr v-if="!loading && filteredItems.length === 0">
                <td colspan="6">No data found.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex flex-wrap justify-content-between align-items-center mt-3">
          <div class="mb-2 mb-md-0">
            Showing {{ meta.from || 0 }} - {{ meta.to || 0 }} of {{ meta.total || 0 }}
          </div>
          <div class="d-flex align-items-center">
            <div class="page-controls">
              <CButton size="sm" class="mr-1" :disabled="(meta.current_page || 1) <= 1" @click="gotoPage((meta.current_page || 1) - 1)">Prev</CButton>
              <CButton
                v-for="pageNumber in visiblePages"
                :key="'p-' + pageNumber"
                size="sm"
                class="mr-1"
                :color="pageNumber === (meta.current_page || 1) ? 'primary' : 'light'"
                @click="gotoPage(pageNumber)"
              >
                {{ pageNumber }}
              </CButton>
              <CButton size="sm" :disabled="(meta.current_page || 1) >= (meta.last_page || 1)" @click="gotoPage((meta.current_page || 1) + 1)">Next</CButton>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import icarePeopleDetail from '@/services/icarePeopleDetail'

export default {
  name: 'IcarePeopleDetail',
  data() {
    return {
      loading: false,
      items: [],
      search: '',
      zone_id: '',
      summary_filter: 'all',
      page: 1,
      per_page: 10,
      per_page_option: '10',
      meta: {},
      zoneOptions: [{ value: '', label: 'All Zones' }],
      summaryFilterOptions: [
        { value: 'all', label: 'All Summary' },
        { value: 'pending_planted', label: 'Pending planted > 0' },
        { value: 'pending_remove_planted', label: 'Pending remove planted > 0' },
        { value: 'remove_cleanup', label: 'Remove visitor / cleanup > 0' }
      ],
      perPageOptions: [
        { value: '10', label: '10' },
        { value: '50', label: '50' },
        { value: '100', label: '100' },
        { value: 'all', label: 'All' }
      ]
    }
  },
  computed: {
    filteredItems() {
      if (this.summary_filter === 'all') return this.items
      return this.items.filter((item) => {
        const summary = item && item.summary ? item.summary : {}
        if (this.summary_filter === 'pending_planted') return this.safeCount(summary.pending_planted_count) > 0
        if (this.summary_filter === 'pending_remove_planted') return this.safeCount(summary.pending_remove_planted_count) > 0
        if (this.summary_filter === 'remove_cleanup') return this.safeCount(summary.pending_remove_visitor_count) > 0
        return true
      })
    },
    visiblePages() {
      const current = this.meta.current_page || 1
      const last = this.meta.last_page || 1
      const start = Math.max(1, current - 2)
      const end = Math.min(last, current + 2)
      const pages = []
      for (let p = start; p <= end; p++) pages.push(p)
      return pages
    }
  },
  mounted() {
    this.loadData()
  },
  watch: {
    summary_filter(newValue) {
      if (newValue !== 'all') {
        this.per_page_option = 'all'
      }
      this.page = 1
      this.loadData()
    }
  },
  methods: {
    applyFilters() {
      this.page = 1
      this.loadData()
    },
    resetFilters() {
      this.search = ''
      this.zone_id = ''
      this.summary_filter = 'all'
      this.page = 1
      this.loadData()
    },
    loadData() {
      this.loading = true
      const requestedPerPage = this.per_page_option === 'all'
        ? Math.max(this.meta.total || 0, 1000)
        : Number(this.per_page_option || 10)

      this.per_page = requestedPerPage

      icarePeopleDetail
        .getIcareList({
          page: this.page,
          per_page: this.per_page,
          search: this.search,
          zone_id: this.zone_id || undefined
        })
        .then((res) => {
          const payload = res.data || {}
          this.items = payload.data || []
          this.meta = {
            current_page: payload.current_page || 1,
            last_page: payload.last_page || 1,
            total: payload.total || 0,
            from: payload.from || 0,
            to: payload.to || 0
          }
          this.syncZoneOptions()
        })
        .catch((err) => {
          this.items = []
          alert((err.response && err.response.data && err.response.data.message) || 'Failed loading data')
        })
        .finally(() => {
          this.loading = false
        })
    },
    syncZoneOptions() {
      const map = {}
      const options = [{ value: '', label: 'All Zones' }]
      this.items.forEach((item) => {
        if (!item || !item.zone_id) return
        if (map[item.zone_id]) return
        map[item.zone_id] = true
        options.push({
          value: String(item.zone_id),
          label: item.zone_name ? `${item.zone_name} (${item.zone_id})` : `Zone ${item.zone_id}`
        })
      })

      if (this.zone_id && !options.some((opt) => opt.value === String(this.zone_id))) {
        options.push({ value: String(this.zone_id), label: `Zone ${this.zone_id}` })
      }

      this.zoneOptions = options
    },
    openDetail(icareId) {
      this.$router.push({ path: '/icare/icare_people_detail/' + icareId })
    },
    gotoPage(pageNumber) {
      const target = Number(pageNumber)
      if (!Number.isInteger(target)) return
      if (target < 1 || target > (this.meta.last_page || 1)) return
      this.page = target
      this.loadData()
    },
    handlePerPageChange() {
      this.page = 1
      this.loadData()
    },
    safeCount(value) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed)) return 0
      return parsed
    },
    summaryTooltip(label, count, definition, recalculatedAt) {
      return `${label}: ${this.safeCount(count)}\nDefinition: ${definition}\nLast recalculated: ${recalculatedAt || '-'}`
    },
    initial(name) {
      if (!name) return 'I'
      return String(name).trim().charAt(0).toUpperCase()
    }
  }
}
</script>

<style scoped>
.filter-panel {
  border: 1px solid #d8dee8;
  border-radius: 10px;
  padding: 14px;
  background: #f8fafd;
}
.total-card {
  border: 1px solid #d8dee8;
  border-radius: 12px;
  padding: 10px 12px;
  background: #fff;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 140px;
}
.total-card-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.total-card-value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: #1f2937;
}
.filter-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 6px;
}
.legend-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.legend-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  font-size: 12px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  border: 1px solid #9da5b1;
}
.dot-green { background: #2eb85c; }
.dot-yellow { background: #f9b115; }
.dot-red { background: #e55353; }
.icare-photo {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #d8dee8;
}
.icare-photo-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dce4ef;
  color: #3c4b64;
  font-weight: 700;
}
.summary-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.summary-dot {
  min-width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  padding: 0 6px;
}
.per-page-select {
  width: 90px;
}
.page-controls {
  display: flex;
  align-items: center;
}
</style>
